import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPERIENCING = [
    "Love or relationship interference",
    "A third party or betrayal",
    "Blocked fate or missed opportunities",
    "Jealousy, obsession or unwanted attachment",
    "Repeating bad luck or unexplained setbacks",
    "Negative energy or suspected spiritual interference",
    "Family conflict or generational patterns",
    "Separation from someone you feel destined for",
    "Something else",
];

const ENERGY = [
    "Heavy or draining",
    "Confusing or deceptive",
    "Obsessive or difficult to escape",
    "Blocked or stagnant",
    "Angry or hostile",
    "Unnatural or spiritually influenced",
    "I cannot explain it",
];

const inputCls =
    "w-full rounded-xl border border-purple-500/30 bg-[#0b031e]/70 px-4 py-3 text-sm text-[#f8f7ff] placeholder:text-[#c77dff]/40 transition-[border-color,box-shadow] duration-300 focus:border-[#c77dff]/70 focus:shadow-[0_0_20px_rgba(199,125,255,0.25)] focus:outline-none";

const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
    });

const chipCls = (active) =>
    `rounded-full border px-4 py-2 font-cinzel text-[11px] tracking-[0.12em] transition-[border-color,box-shadow,color,background-color] duration-300 ${
        active
            ? "border-[#c77dff]/70 bg-[#5a189a]/50 text-[#f8f7ff] shadow-[0_0_18px_rgba(199,125,255,0.35)]"
            : "border-purple-500/25 text-[#e0aaff]/60 hover:border-[#c77dff]/50 hover:text-[#e0aaff]"
    }`;

export const UniqueSection = ({ onEnter }) => {
    const [form, setForm] = useState({ first_name: "", email: "", story: "", outcome: "", energy: "" });
    const [experiencing, setExperiencing] = useState([]);
    const [photos, setPhotos] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const toggle = (item) =>
        setExperiencing((xs) => (xs.includes(item) ? xs.filter((x) => x !== item) : [...xs, item]));

    const onPhotos = async (e) => {
        const files = [...(e.target.files || [])];
        e.target.value = "";
        for (const f of files.slice(0, 2 - photos.length)) {
            if (f.size > 2_000_000) {
                toast.error(`"${f.name}" is too heavy for the mirror — 2MB is the limit.`);
                continue;
            }
            const url = await fileToDataUrl(f);
            setPhotos((p) => [...p, url].slice(0, 2));
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!experiencing.length) {
            toast.error("Choose at least one thing you are experiencing.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await axios.post(`${API}/assessments`, {
                ...form,
                experiencing,
                attachments: photos,
            });
            toast.success("Submitted. Mara will personally examine what surrounds your situation.");
            onEnter(res.data.conversation_id);
        } catch (err) {
            toast.error(err.response?.data?.detail || "The mirror clouded. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="unique" data-testid="unique-section" className="relative px-5 py-28 sm:px-8 lg:py-36">
            <div className="nebula-blob right-[5%] top-[10%] h-[26rem] w-[26rem] bg-[#7b2cbf]/20" />
            <div className="mx-auto max-w-3xl">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9 }}
                    className="mb-12 text-center"
                >
                    <p className="eyebrow mb-4">Chapter VI · The Custom Working</p>
                    <h2 className="text-glow-lilac font-display text-2xl font-bold leading-snug tracking-wide text-[#f8f7ff] sm:text-3xl lg:text-4xl">
                        IS YOUR SITUATION UNIQUE?
                    </h2>
                    <p className="mx-auto mt-6 max-w-xl font-cormorant text-lg italic leading-relaxed text-[#e0aaff]/80">
                        Not every spiritual disturbance follows the same pattern. Tell Mara what has been happening so she
                        can identify the energy surrounding your situation and prepare a custom reading and ritual
                        specifically for you.
                    </p>
                </motion.div>

                <motion.form
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.9, delay: 0.15 }}
                    onSubmit={submit}
                    data-testid="unique-form"
                    className="glass-card relative overflow-hidden p-8 sm:p-10"
                >
                    <div className="mirror-sheen pointer-events-none absolute inset-0 opacity-20" />
                    <div className="relative space-y-8">
                        <div className="grid gap-5 sm:grid-cols-2">
                            <div>
                                <label htmlFor="unique-name" className="eyebrow mb-2 block">First name</label>
                                <input id="unique-name" required value={form.first_name} onChange={set("first_name")} data-testid="unique-input-name" placeholder="What this realm calls you" className={inputCls} />
                            </div>
                            <div>
                                <label htmlFor="unique-email" className="eyebrow mb-2 block">Email</label>
                                <input id="unique-email" required type="email" value={form.email} onChange={set("email")} data-testid="unique-input-email" placeholder="Where Mara's answer can find you" className={inputCls} />
                            </div>
                        </div>

                        <div>
                            <p className="eyebrow mb-1">What are you experiencing?</p>
                            <p className="mb-3 text-xs italic text-[#c77dff]/60">Select all that apply</p>
                            <div className="flex flex-wrap gap-2.5">
                                {EXPERIENCING.map((x) => (
                                    <button key={x} type="button" onClick={() => toggle(x)} data-testid={`unique-chip-${x.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={chipCls(experiencing.includes(x))}>
                                        {x}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label htmlFor="unique-story" className="eyebrow mb-2 block">Tell Mara your story</label>
                            <textarea id="unique-story" required rows={4} value={form.story} onChange={set("story")} data-testid="unique-input-story" placeholder="What has been happening, when did it begin, who is involved, and what changes have you noticed?" className={`${inputCls} resize-none`} />
                        </div>

                        <div>
                            <label htmlFor="unique-outcome" className="eyebrow mb-2 block">What do you want to change?</label>
                            <textarea id="unique-outcome" rows={3} value={form.outcome} onChange={set("outcome")} data-testid="unique-input-outcome" placeholder="Tell Mara the outcome you are seeking or what you feel should be restored to you." className={`${inputCls} resize-none`} />
                        </div>

                        <div>
                            <p className="eyebrow mb-3">How does the energy feel?</p>
                            <div className="flex flex-wrap gap-2.5">
                                {ENERGY.map((x) => (
                                    <button key={x} type="button" onClick={() => setForm((f) => ({ ...f, energy: x }))} data-testid={`unique-energy-${x.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={chipCls(form.energy === x)}>
                                        {x}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <p className="eyebrow mb-1">Uploads — optional</p>
                            <p className="mb-3 text-xs italic text-[#c77dff]/60">
                                Add photographs, screenshots or other details that may help Mara understand the situation.
                            </p>
                            <div className="flex items-center gap-4">
                                <input type="file" accept="image/*" multiple onChange={onPhotos} data-testid="unique-photo-input" className="hidden" id="unique-photos" />
                                <label htmlFor="unique-photos" data-testid="unique-photo-button" className="btn-ghost-glow flex cursor-pointer items-center gap-2">
                                    <ImagePlus className="h-4 w-4" /> ADD IMAGES ({photos.length}/2)
                                </label>
                                {photos.map((p, i) => (
                                    <span key={i} className="relative">
                                        <img src={p} alt="" className="h-14 w-14 rounded-lg border border-purple-500/25 object-cover" />
                                        <button type="button" onClick={() => setPhotos((ps) => ps.filter((_, j) => j !== i))} aria-label="Remove image" className="absolute -right-1.5 -top-1.5 rounded-full bg-[#0b031e] p-0.5 text-[#e0aaff]">
                                            <X className="h-3 w-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>

                        <p className="font-cormorant text-sm italic leading-relaxed text-[#e0aaff]/70">
                            Once submitted, Mara will personally examine your answers, identify the spiritual current
                            surrounding the situation and recommend a custom reading and ritual created around your
                            circumstances.
                        </p>

                        <button type="submit" disabled={submitting} data-testid="unique-submit-button" className="btn-glow relative w-full disabled:opacity-70">
                            <span className="mirror-sheen absolute inset-0 animate-shimmer-line" />
                            <span className="relative">{submitting ? "OPENING…" : "REVEAL WHAT I'M DEALING WITH"}</span>
                        </button>
                    </div>
                </motion.form>
            </div>
        </section>
    );
};
